import { Injectable } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { exhaustMap, take, finalize } from 'rxjs/operators';
import { SignalRHubService } from '../signalr.hub.service';

export interface IRetryPolicy {
    delayMs: number;
    maxRetries: number;
}

@Injectable({ providedIn: 'root' })
export abstract class CommunicationHubService extends SignalRHubService {
    readonly #retryMap = new Map<
        string,
        { count: number; subject: Subject<void>; completed: boolean }
    >();

    protected tryReconnectToConversation(
        conversationId: string,
        reconnectFn: () => void,
        policy: IRetryPolicy,
        onMaxRetriesReached?: (conversationId: string) => void,
    ) {
        const retryEntry = this.#retryMap.get(conversationId);

        if (retryEntry?.completed) return;

        if (!retryEntry) {
            const subject = new Subject<void>();
            this.#retryMap.set(conversationId, {
                count: 0,
                subject,
                completed: false,
            });

            subject
                .pipe(
                    exhaustMap(() => timer(policy.delayMs)),
                    take(policy.maxRetries),
                    finalize(() => {
                        const data = this.#retryMap.get(conversationId);
                        if (data) {
                            data.completed = true;
                            this.#retryMap.set(conversationId, data);
                        }
                        onMaxRetriesReached?.(conversationId);
                    }),
                )
                .subscribe(() => {
                    reconnectFn();
                });
        }

        const data = this.#retryMap.get(conversationId);
        if (!data || data.count >= policy.maxRetries || data.completed) {
            return;
        }

        data.count++;
        data.subject.next();
    }
}
